<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed, nextTick } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import type {
	IMercariProductListFetchCompleteEvent,
	IMercariProductListItemClickEvent,
} from '@/components/MercariProductList.vue'
import { useMercariFilter } from '@/lib/useMercariFilter'
import { getSortingMethodLabel } from '@/lib/mercariFilterConfig'
import MercariProductListingSection from '@/components/MercariProductListingSection.vue'
import type { IMercariProductListItemContent } from '@/components/MercariProductListItem.vue'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import type { IBreadcrumbItem } from '@/types/BreadCrumb'
import { getMercariUrl } from '@/lib/mercari'
import getRedirectSeoLink from '@/lib/getSeoRedirect'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import {
	fetchMercariBrandCollections,
	type IMercariBrand,
	type IMercariBrandCollection,
} from '@/assets/ts/apiManagement'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { useBreakpoints } from '@/lib/utils'

const { isLarge } = useBreakpoints()

const props = defineProps<{
	pageContent: {
		breadcrumbs: {
			home: string
			shopping: string
			mercari: string
		}
		resultsTitle: string
		productItem: IMercariProductListItemContent
		productListLoadMoreButtonText: string
		edgeCase: {
			empty: {
				description: string
			}
			serverError: {
				title: string
				description: string
				buttonText: string
			}
		}
		filterContent: any
		collections: {
			title: string
			viewMore: string
			viewLess: string
		}
	}
}>()

/**
 * Gets the brand slug from URL
 * - In local environment: reads from 'brand_slug' query parameter
 * - In production: extracts from URL path (e.g., /mercari/brands/pokemon → pokemon)
 */
const getBrandSlugFromUrl = (): string => {
	if (import.meta.env.PROD) {
		const path = window.location.pathname
		const slug = path.split('/').filter(Boolean).pop()
		return slug ?? ''
	}
	const params = new URLSearchParams(window.location.search)
	return params.get('brand_slug') || ''
}

const brandSlug = ref(getBrandSlugFromUrl())
const brandData = ref<IMercariBrand | null>(null)
const brandCollections = ref<IMercariBrandCollection[]>([])
const searchKeyword = ref('')
const isLoading = ref(true)
const hasError = ref(false)

// Mobile collections expand/collapse state
const isCollectionsExpanded = ref(false)
const shouldShowCollectionsToggle = ref(false)
const isCollectionsReady = ref(false)
const collectionsRef = ref<HTMLDivElement | null>(null)
const collapsedHeight = ref(0)

const productSectionRef =
	ref<InstanceType<typeof MercariProductListingSection>>()

const productListRef = computed(() => productSectionRef.value?.productListRef)

const mercariFilter = useMercariFilter({
	productListRef,
	locationId: GaGeneralPageName.MercariBrandListing,
	filterContent: props.pageContent.filterContent,
	keyword: searchKeyword.value,
})

const isTranslated = ref(true)

const brandName = computed(() => brandData.value?.name || '')

const breadcrumbs = computed<IBreadcrumbItem[]>(() => [
	{
		displayName: props.pageContent.breadcrumbs.home,
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: props.pageContent.breadcrumbs.shopping,
		categoryId: 'shopping',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('proxyShopping')}/`,
	},
	{
		displayName: props.pageContent.breadcrumbs.mercari,
		categoryId: 'mercari',
		link: getMercariUrl('index'),
	},
	{
		displayName: brandName.value,
		categoryId: 'brand',
		link: '',
	},
])

const collectionsTitle = computed(() => {
	const template = props.pageContent.collections.title
	return template.replace('{BRAND_NAME}', brandName.value)
})

const hasCollections = computed(() => brandCollections.value.length > 0)

const getCollectionUrl = (collection: IMercariBrandCollection): string => {
	return getMercariUrl('collection-listing', {
		brand_slug: brandSlug.value,
		collection_slug: collection.slug,
	})
}

const toggleCollectionsExpanded = () => {
	isCollectionsExpanded.value = !isCollectionsExpanded.value
}

/**
 * Calculate the height needed to display the complete first row of collection items.
 */
const calculateFirstRowHeight = (): number => {
	const container = collectionsRef.value
	if (!container) return 0

	const firstItem = container.querySelector('a')
	if (!firstItem) return 0

	// Use calculation result instead of getBoundingClientRect().height in case there is padding above first item
	const containerTop = container.getBoundingClientRect().top
	return firstItem.getBoundingClientRect().bottom - containerTop
}

/**
 * Checks if collections would overflow when collapsed (first row only).
 */
const checkCollectionsOverflow = async () => {
	const element = collectionsRef.value
	if (!element) {
		isCollectionsReady.value = true
		return
	}

	// Remove constraint to measure natural state (collapsedHeight = 0 means no limit)
	collapsedHeight.value = 0
	await nextTick()

	const firstRowHeight = calculateFirstRowHeight()
	const wouldOverflow = element.scrollHeight > firstRowHeight

	collapsedHeight.value = firstRowHeight
	shouldShowCollectionsToggle.value = wouldOverflow
	isCollectionsReady.value = true
}

const throttledCheckCollectionsOverflow = useThrottleFn(
	checkCollectionsOverflow,
	200,
	true, // trailing: true
)

const handleCollectionClick = (collection: IMercariBrandCollection) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrandListing,
		button_name: 'Hot Brands Collection',
		event_extra: collection.collection_id,
	})
}

const fetchBrandData = async () => {
	if (!brandSlug.value) {
		hasError.value = true
		isLoading.value = false

		window.location.href = getMercariUrl('index')
		return
	}

	try {
		isLoading.value = true
		hasError.value = false

		const data = await fetchMercariBrandCollections(brandSlug.value)

		brandData.value = data.brand
		brandCollections.value = (data.collections || []).slice(0, 10)

		searchKeyword.value = data.brand.search_query || ''
	} catch (error) {
		console.error('Failed to fetch brand data:', error)
		hasError.value = true
	} finally {
		isLoading.value = false
	}
}

const handleItemClick = (data: IMercariProductListItemClickEvent) => {
	const filterValues = mercariFilter.getFilterTrackingValues()

	const categoryValues = mercariFilter.categoryLevels?.value
		? mercariFilter.createCategoryValuesFromLevels(
				mercariFilter.categoryLevels.value,
		  )
		: {}

	const sortingMethodLabel = getSortingMethodLabel(
		mercariFilter.filterState.sort,
	)

	createGAEvent('select_item', {
		page_name: GaGeneralPageName.MercariBrandListing,
		event_extra: brandData.value?.brand_id,
		sorting_method: sortingMethodLabel,
		item_category: categoryValues.item_category || '',
		item_category2: categoryValues.item_category2 || '',
		item_category3: categoryValues.item_category3 || '',
		item_category4: categoryValues.item_category4 || '',
		filter_condition: filterValues.filter_condition || '',
		filter_availability: filterValues.filter_availability || '',
		filter_price: filterValues.filter_price || '',
		filter_lister: filterValues.filter_lister || '',
		items: [
			{
				location_id: GaGeneralPageName.MercariIndex,
				item_list_name: 'Product Listing',
				item_id: `product_${data.item.id}`,
				item_name: data.item.name,
				item_category: 'Mercari',
				index: data.index + 1,
			},
		],
	})

	window.open(data.targetUrl, '_blank')
}

onMounted(async () => {
	await fetchBrandData()

	await mercariFilter.initialize()

	// Check collections overflow after content is rendered
	await nextTick()
	checkCollectionsOverflow()

	window.addEventListener('resize', throttledCheckCollectionsOverflow)

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariBrandListing,
		event_extra: brandData.value?.brand_id,
	})
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', throttledCheckCollectionsOverflow)
})
</script>

<template>
	<section class="bs-container">
		<div class="col-span-12 pb-[37px] mdp:pb-[60px] lg:mt-32px">
			<div v-if="!isLoading" class="flex flex-col gap-16px lg:gap-24px">
				<!-- Breadcrumb -->
				<PageBreadCrumb
					:breadcrumbs="breadcrumbs"
					:is-override-seo-check="true"
					class="p-0"
					is-last-item-self
				/>

				<!-- Top Collections Section -->
				<div
					class="flex flex-col gap-16px lg:gap-24px"
					:class="{ invisible: !isCollectionsReady }"
				>
					<!-- Brand title -->
					<h1
						class="text-n-950"
						:class="isLarge ? 'typography-h3-str' : 'typography-h4-str'"
					>
						{{ brandName }}
					</h1>

					<!-- Collections container -->
					<div
						v-if="hasCollections"
						class="flex flex-col gap-16px rounded-lg border border-n-100 p-16px"
					>
						<!-- Collections title -->
						<h2
							class="text-n-950"
							:class="isLarge ? 'typography-s1-str' : 'typography-h5-str'"
						>
							{{ collectionsTitle }}
						</h2>

						<!-- Collections tags -->
						<div
							ref="collectionsRef"
							class="flex flex-wrap gap-16px"
							:style="
								!isCollectionsExpanded && !isLarge && collapsedHeight > 0
									? { maxHeight: `${collapsedHeight}px`, overflow: 'hidden' }
									: {}
							"
						>
							<a
								v-for="collection in brandCollections"
								:key="collection.slug"
								:href="getCollectionUrl(collection)"
								target="_blank"
								rel="noopener noreferrer"
								@click="handleCollectionClick(collection)"
								class="typography-c1-reg cursor-pointer rounded-full bg-n-50 px-8px py-[2px] text-n-800 transition-colors lg:px-12px lg:py-4px"
							>
								{{ collection.name }}
							</a>
						</div>

						<!-- View more/less button (mobile only, if overflows) -->
						<button
							v-if="!isLarge && shouldShowCollectionsToggle"
							@click="toggleCollectionsExpanded"
							class="flex w-fit items-center gap-4px text-n-950"
						>
							<span class="typography-l2-emp">
								{{
									isCollectionsExpanded
										? pageContent.collections?.viewLess || 'View less'
										: pageContent.collections?.viewMore || 'View more'
								}}
							</span>
							<BaseIcon
								name="chevron_thin"
								class="h-16px w-16px fill-o-500 transition-transform"
								:class="{
									'rotate-[-90deg]': isCollectionsExpanded,
									'rotate-90': !isCollectionsExpanded,
								}"
							/>
						</button>
					</div>
				</div>

				<!-- Main content -->
				<main>
					<MercariProductListingSection
						ref="productSectionRef"
						:key="searchKeyword"
						:filter-instance="mercariFilter"
						:filter-content="props.pageContent.filterContent"
						:keyword="searchKeyword"
						shop-id=""
						:item-content="props.pageContent.productItem"
						:load-more-button-text="
							props.pageContent.productListLoadMoreButtonText
						"
						:edge-case="props.pageContent.edgeCase"
						:is-translated="isTranslated"
						:location-id="GaGeneralPageName.MercariBrandListing"
						:hide-category-filter="true"
						@item-click="handleItemClick"
					>
						<template #search-result-title>
							<div
								class="hidden items-center justify-center mdp:flex mdp:justify-start"
							>
								<div class="typography-s1-str text-n-500">
									{{ pageContent.resultsTitle }}
								</div>
							</div>
						</template>
					</MercariProductListingSection>
				</main>
			</div>
		</div>
	</section>
</template>
